import { computePosition, offset, shift, autoUpdate, flip } from "@floating-ui/dom";
import { createEffect, For, Match, on, onCleanup, Show, Switch } from "solid-js";
import { Portal } from "solid-js/web";
import type { AssetWithActions } from "@storytell/cp-client/src/gen/assets/v3/asset_service_pb";
import type { useMentionsState } from "./useMentionsState";
import { TbCheck, TbFile, TbFileText, TbLoader2 } from "solid-icons/tb";

export const MentionsPickerPopup = (props: {
  mentionsState: ReturnType<typeof useMentionsState>;
}) => {
  const state = props.mentionsState.mentionsState;
  let el!: HTMLDivElement;

  const updatePosition = (referenceEl: HTMLElement, floatingEl: HTMLElement) => {
    computePosition(referenceEl, floatingEl, {
      placement: "right-start",
      middleware: [offset(20), flip(), shift()],
    }).then((c) => {
      Object.assign(el.style, {
        left: `${c.x}px`,
        top: `${c.y}px`,
      });
    });
  };
  createEffect(
    on(
      () => state.domAnchor,
      (domAnchor) => {
        if (!domAnchor) return;
        const cleanup = autoUpdate(domAnchor, el, () => updatePosition(domAnchor, el));
        onCleanup(() => cleanup());
      },
    ),
  );

  // Close on outside click, even if overlays intercept
  createEffect(() => {
    if (!state.open) return;
    const handlePointerDown = (e: PointerEvent) => {
      // If el is not mounted yet, or click is inside the popup, do nothing
      if (!el || el.contains(e.target as Node)) return;
      props.mentionsState.dismiss();
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("pointerdown", handlePointerDown, true); // use capture phase
    onCleanup(() => window.removeEventListener("pointerdown", handlePointerDown, true));
  });

  return (
    <Show when={state.open}>
      <Portal mount={document.body}>
        <div class="absolute inset-0" on:click={() => props.mentionsState.dismiss()} />
        <div
          ref={el}
          class="z-20 bg-tile border border-primary-border rounded-lg shadow-lg absolute p-1 max-h-[50vh] overflow-y-auto max-w-[80vw]"
          on:mousemove={() => state.lastInteraction !== "mouse" && props.mentionsState.setLastInteraction("mouse")}
        >
          <Switch>
            <Match when={state.loading}>
              <div class="px-2 py-1 bg-panel rounded flex items-center gap-2 min-w-56">
                <TbLoader2 class="animate-spin size-4 text-muted-text" />
                <span>Loading</span>
              </div>
            </Match>
            <Match when={state.error}>
              <button
                type="button"
                onClick={() => props.mentionsState.retry()}
                class="min-h-0 px-2 py-1 bg-panel rounded flex items-center gap-2 min-w-56"
              >
                <p>
                  There was an error. <span class="font-medium">Retry?</span>
                </p>
                <TbCheck class="size-4 text-muted-text" />
              </button>
            </Match>
            <Match when={state.data.length === 0}>
              <button
                type="button"
                onClick={() => props.mentionsState.dismiss()}
                class="min-h-0 px-2 py-1 bg-panel rounded flex items-center gap-2 min-w-56"
              >
                <p>
                  No results found. <span class="font-medium">Dismiss?</span>
                </p>
                <TbCheck class="size-4 text-muted-text" />
              </button>
            </Match>

            <Match when={state.data.length > 0}>
              <div class="flex flex-col gap-2">
                <Show when={state.data.length > 0}>
                  <span class="text-muted-text text-sm select-none ml-2 mt-2">Assets</span>
                </Show>
                <For each={state.data}>
                  {(d, index) => (
                    <button
                      on:mouseenter={() =>
                        state.lastInteraction === "mouse" && props.mentionsState.setActiveIndex(index())
                      }
                      onClick={() => props.mentionsState.select(index())}
                      class="min-h-0 rounded px-2 py-1 min-w-56"
                      classList={{
                        "bg-canvas": state.activeIndex === index(),
                      }}
                      type="button"
                    >
                      <MentionAsset asset={d.data} active={state.activeIndex === index()} />
                    </button>
                  )}
                </For>
              </div>
            </Match>
          </Switch>
        </div>
      </Portal>
    </Show>
  );
};

const MentionAsset = (props: {
  asset: AssetWithActions;
  active: boolean;
}) => {
  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });

  return (
    <div ref={ref} class="flex items-center gap-2">
      <TbFileText
        data-active={props.active}
        class="size-4 text-muted-text data-[active=true]:scale-hover transition-transform"
      />
      <div class="flex flex-col text-left max-w-full overflow-hidden">
        <p class="font-medium truncate">{props.asset.data.originalFilename}</p>
        <p class="text-muted-text text-xs truncate">{props.asset.data.displayName}</p>
      </div>
    </div>
  );
};
